import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { Chart, ChartTypes } from "@uicore";
import dayjs from "dayjs";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { IconChevronDown } from "@tabler/icons-react";
import { CheckColoredIcon } from "@assets/icons";
import { cn } from "@/lib/utils";
import { RelativeComponentLoader } from "@/components/loader";
import { Bar } from "recharts";
import { createColorMap } from "@/pages/query/common";
import { useAppState } from "@/modules/app/useAppContext";
import { formatNumber } from "@uicore";
import ComputePoolsTooltip, { COMPUTE_POOLS_TOTAL_KEY } from "./ComputePoolsTooltip";
import { getComputePoolsGraphData } from "@/helpers/apis";

const DailyCostGraphs = ({
  poolNames,
  startDate,
  endDate,
  defaultSelected,
}: {
  poolNames: string[];
  startDate: Date;
  endDate: Date;
  defaultSelected: string[];
}) => {
  const [selectedLegends, setSelectedLegends] = useState<string[]>([]);
  const [allLegends, setAllLegends] = useState<string[]>([]);
  const [legendsOpen, setLegendsOpen] = useState(false);
  const { currency } = useAppState();

  const isoStart = getDateByStartOfDayWithoutTimeoffset(startDate).toISOString();
  const isoEnd = getDateByEndOfDayWithoutTimeoffset(endDate)!.toISOString();
  const { data: graphData, isLoading } = useQuery({
    queryKey: ["compute-pools-graph", isoStart, isoEnd],
    queryFn: () => getComputePoolsGraphData({ start_date: isoStart, end_date: isoEnd }),
  });

  const legends = useMemo(
    () => (selectedLegends.length ? selectedLegends : allLegends),
    [selectedLegends, allLegends]
  );

  // Inline a synthetic per-row total over the rendered pools so the shared
  // CostChartTooltip can read it for its "Total Cost" badge.
  const chartData = useMemo(() => {
    if (!graphData) return [] as Record<string, string | number>[];
    return graphData.map(item => {
      const total = legends.reduce((sum, name) => sum + (Number(item.pool_spends[name]) || 0), 0);
      return { date: item.date, ...item.pool_spends, [COMPUTE_POOLS_TOTAL_KEY]: total };
    });
  }, [graphData, legends]);

  // Always derive legends from poolNames provided by metadata to ensure picker has data
  useEffect(() => {
    setAllLegends(poolNames);
  }, [poolNames]);

  useEffect(() => {
    if (selectedLegends.length === 0 && defaultSelected.length > 0) {
      setSelectedLegends(defaultSelected);
    }
  }, [defaultSelected, selectedLegends.length]);

  const colorsMap = useMemo(() => createColorMap(allLegends), [allLegends]);

  if (isLoading) return <RelativeComponentLoader label="Loading daily costs..." />;

  return (
    <Card role="region" aria-label="Daily Cost Graph">
      <CardHeader>
        <Stack align={"center"} justify={"between"}>
          <Typography variant={"section-heading"}>Compute Pool Daily Cost</Typography>
          <Stack direction={"row"} align={"center"} gap={"md"}>
            <Label className={"al-m-0"}>Legends:</Label>
            <Popover open={legendsOpen} onOpenChange={setLegendsOpen}>
              <PopoverTrigger asChild>
                <Button
                  size={"sm"}
                  variant="outline"
                  role="combobox"
                  aria-expanded={legendsOpen}
                  className="al-w-56 al-justify-between al-py-3 al-px-2 al-overflow-hidden al-text-ellipsis al-whitespace-nowrap"
                >
                  {selectedLegends.length === 0
                    ? "All Pools"
                    : selectedLegends.join(", ").length > 24
                      ? `${selectedLegends.join(", ").slice(0, 24)}...`
                      : selectedLegends.join(", ")}
                  <IconChevronDown className="al-ml-2 al-h-4 al-w-4 al-shrink-0 al-opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="!al-w-56 !al-p-0">
                <Command>
                  <CommandInput placeholder="Search pools..." className="h-9" />
                  <CommandList>
                    <CommandEmpty aria-label="No pools found">No Pool Found.</CommandEmpty>
                    <CommandGroup className={"al-overflow-auto"}>
                      {allLegends.map(p => (
                        <CommandItem
                          key={p}
                          value={p}
                          onSelect={() =>
                            setSelectedLegends(prev =>
                              prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]
                            )
                          }
                          className={"al-cursor-pointer"}
                        >
                          <CheckColoredIcon
                            className={cn(
                              "al-mr-2 al-size-4",
                              selectedLegends.includes(p) ? "al-opacity-100" : "al-opacity-0"
                            )}
                          />
                          {p}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </Stack>
        </Stack>
      </CardHeader>
      <CardContent className={"al-pb-10"}>
        <Chart
          title="Compute Pools Overview"
          xAxisDataKey="date"
          xAxisLabelFormatter={value => dayjs(value).format("DD MMM")}
          yAxisLabelFormatter={value => formatNumber(Number(value), { currency })}
          type={ChartTypes.BarChart}
          data={chartData}
          tooltipProps={{
            content: props => <ComputePoolsTooltip {...props} />,
          }}
          chartProps={{}}
        >
          {({ selectedLegend }) => {
            const numberOfPoints = chartData.length;
            const barSize = Math.max(20, Math.min(120, 600 / Math.max(1, numberOfPoints)));
            return (
              <>
                {legends.map((name, i) => (
                  <Bar
                    key={name}
                    barSize={barSize}
                    dataKey={name}
                    name={name}
                    stackId="cost"
                    radius={i === legends.length - 1 ? [5, 5, 0, 0] : [0, 0, 0, 0]}
                    fill={colorsMap[name]}
                    hide={Boolean(selectedLegend && selectedLegend !== name)}
                  />
                ))}
              </>
            );
          }}
        </Chart>
      </CardContent>
    </Card>
  );
};

export default DailyCostGraphs;
